/**
 * Business chat quick reply suggestions (ported from Flutter business_chat_suggestions_helper).
 * Used when conversation type is business; suggestions depend on last message and user type.
 * - userType "user" = customer
 * - userType "owner" | "teamMember" = business side (same suggestions)
 * Message type "add-remove" is not considered as a message (informational only).
 */

// Emoji lists for matching
const THUMBS_UP_EMOJIS = ["👍", "🙏", "👌", "👋"];
const HEART_CHECK_EMOJIS = ["❤", "✅", "❎"];
const HAPPY_EMOJIS = ["😊", "😄", "🙂", "😃", "😁"];
const FOOD_EMOJIS = ["🍕", "🍔", "🍜", "🍲", "🥘", "🍛"];
const DELIVERY_EMOJIS = ["🚗", "🛵", "🚚", "⏰", "⏱"];
const SAD_EMOJIS = ["😞", "😔", "😢", "😕", "😟"];
const FIRE_EMOJIS = ["🔥", "🌶"];
const STAR_EMOJIS = ["⭐", "🌟", "rating"];
const MONEY_EMOJIS = ["💰", "💵", "💳"];
const QUESTION_EMOJIS = ["❓", "❔"];

function containsAnyEmoji(message, emojis) {
  return emojis.some((emoji) => message.includes(emoji));
}

// Regex patterns (case-insensitive where needed via .toLowerCase() before match)
const PATTERNS = {
  greeting: /\b(hi|hii|hello|hey|greetings|awake|hiii)\b/i,
  morning: /\b(good morning)\b/i,
  afternoon: /\b(good afternoon)\b/i,
  evening: /\b(good evening)\b/i,
  night: /\b(good night)\b/i,
  orderStatus: /\b(order status|track order|where is my order|order tracking)\b/i,
  orderModify: /\b(modify order|change order|update order|edit order)\b/i,
  repeatOrder: /\b(repeat order|same order|again|previous order|last time|reorder)\b/i,
  orderGeneral: /\b(order|buy|purchase)\b/i,
  delivery: /\b(deliver|delivery|reach|arrive|coming|dispatch)\b/i,
  menu: /\b(menu|catalog|list|food items|item list)\b/i,
  offers: /\b(offer|offers|discount|combo|combos|deal|deals|promotion)\b/i,
  specials: /\b(special|specials|specialty|best|recommend|famous|suggestion|bestseller)\b/i,
  variety: /\b(variety|varieties|type|types|option|options|choice|different)\b/i,
  taste: /\b(taste|tasty|delicious|yummy|spicy|hot|cold|fresh)\b/i,
  positiveFeedback: /\b(good|great|excellent|amazing|awesome|loved|perfect|wonderful|fantastic)\b/i,
  negativeFeedback: /\b(bad|poor|worst|terrible|awful|hate|disappointed|unsatisfied)\b/i,
  dietary: /\b(veg|vegetarian|non-veg|nonveg|jain|halal|kosher|vegan)\b/i,
  allergy: /\b(allerg|ingredient|contain|nuts|gluten|dairy|egg|lactose)\b/i,
  size: /\b(size|large|small|medium|customize|custom|portion)\b/i,
  quantity: /\b(more|less|extra|add|remove|quantity|increase|decrease)\b/i,
  payment: /\b(pay|payment|upi|cash|card|online|wallet|paytm|gpay|phonepe)\b/i,
  refund: /\b(refund|return|money back|reimburse|chargeback)\b/i,
  bill: /\b(bill|invoice|receipt|total|amount)\b/i,
  price: /\b(price|cost|how much|rate|charge|fee)\b/i,
  availability: /\b(available|stock|ready|in stock|out of stock)\b/i,
  timing: /\b(timing|hours|open|close|schedule|when open|what time)\b/i,
  location: /\b(address|location|where|direction|map|navigate)\b/i,
  booking: /\b(book|booking|reserve|reservation|table)\b/i,
  confirm: /\b(confirm|confirmed|yes|sure|done|ok|okay|okkk)\b/i,
  ok: /\b(ok|okay|okkk|okkkk)\b/i,
  cancel: /\b(cancel|cancelled|abort)\b/i,
  waiting: /\b(wait|waiting|hold|busy|taking long|how long)\b/i,
  issue: /\b(issue|problem|wrong|delay|complaint|error|mistake)\b/i,
  urgency: /\b(urgent|emergency|asap|quickly|fast|hurry|immediate)\b/i,
  thanks: /\b(thank|thanks|thx|tks|grateful|appreciate)\b/i,
  contact: /\b(contact|call|phone|number|email|support|help)\b/i,
};

/**
 * Get quick reply suggestions for business chat.
 * @param {Object} options
 * @param {string} [options.lastMessage] - Text of last message (excluding add-remove type).
 * @param {boolean} [options.isEmpty=false] - True if there are no (considered) messages.
 * @param {string} [options.userType='user'] - 'user' (customer), 'owner', or 'teamMember'. Owner and teamMember get same suggestions.
 * @returns {string[]} Array of suggestion strings.
 */
export function getBusinessChatSuggestions({
  lastMessage,
  isEmpty = false,
  userType = "user",
} = {}) {
  const isCustomer = userType === "user";

  if (isEmpty) {
    return isCustomer
      ? ["hi", "hello", "i want to place an order", "is anyone available?"]
      : [
          "hello",
          "how can I help you?",
          "please share your requirement",
          "we are available to assist you",
        ];
  }

  if (!lastMessage || typeof lastMessage !== "string") return [];
  const message = lastMessage.trim().toLowerCase();
  if (!message) return [];

  // Priority 1: Emoji patterns
  if (containsAnyEmoji(message, THUMBS_UP_EMOJIS))
    return ["👍🏻", "🙏🏻", "👌🏻", "👋🏻"];
  if (containsAnyEmoji(message, HEART_CHECK_EMOJIS))
    return ["❤️", "✅", "❎"];
  if (containsAnyEmoji(message, HAPPY_EMOJIS)) {
    return isCustomer
      ? ["😊", "😄", "🙂", "thank you!"]
      : ["😊", "happy to help!", "🙂"];
  }
  if (containsAnyEmoji(message, FOOD_EMOJIS)) {
    return isCustomer
      ? ["🍕", "🍔", "🍜", "i want to order this"]
      : ["🍕", "🍔", "🍜", "available now!"];
  }
  if (containsAnyEmoji(message, DELIVERY_EMOJIS)) {
    return isCustomer
      ? ["when will it arrive?", "⏰", "how long?"]
      : ["on the way! 🚗", "will reach in 15 mins", "⏰"];
  }
  if (containsAnyEmoji(message, SAD_EMOJIS)) {
    return isCustomer
      ? ["i have an issue", "need help", "not satisfied"]
      : ["sorry for the inconvenience 🙏🏻", "let me help you", "we will fix this"];
  }
  if (containsAnyEmoji(message, FIRE_EMOJIS)) {
    return isCustomer
      ? ["🔥", "looks great!", "i want this"]
      : ["🔥", "hot and fresh!", "trending item"];
  }
  if (containsAnyEmoji(message, STAR_EMOJIS)) {
    return isCustomer
      ? ["⭐", "⭐⭐", "⭐⭐⭐", "⭐⭐⭐⭐", "⭐⭐⭐⭐⭐", "excellent!", "great service"]
      : ["thank you! ⭐", "we appreciate it!", "🙏🏻"];
  }
  if (containsAnyEmoji(message, MONEY_EMOJIS)) {
    return isCustomer
      ? ["how much?", "send payment link", "payment done ✅"]
      : ["💰", "payment received", "please pay"];
  }
  if (containsAnyEmoji(message, QUESTION_EMOJIS)) {
    return isCustomer
      ? ["i have a question", "can you help?", "need information"]
      : ["how can I help?", "please ask", "yes?"];
  }

  // Priority 2: Time-based greetings
  if (PATTERNS.morning.test(message))
    return ["good morning ☀️", "good morning!"];
  if (PATTERNS.afternoon.test(message))
    return ["good afternoon", "good afternoon!"];
  if (PATTERNS.evening.test(message))
    return ["good evening", "good evening!"];
  if (PATTERNS.night.test(message))
    return ["good night 🌙", "good night!"];

  // Priority 3: Specific keywords
  if (PATTERNS.orderStatus.test(message)) {
    return isCustomer
      ? [
          "what is the status of my order?",
          "track my order",
          "where is my order?",
          "order ID: ",
        ]
      : [
          "your order is being prepared",
          "order is out for delivery",
          "please share order ID",
          "checking order status...",
        ];
  }
  if (PATTERNS.orderModify.test(message)) {
    return isCustomer
      ? [
          "can i modify my order?",
          "i want to change something",
          "update my order",
          "add items to order",
        ]
      : [
          "what changes do you need?",
          "order can be modified",
          "please share new details",
          "updated ✅",
        ];
  }
  if (PATTERNS.repeatOrder.test(message)) {
    return isCustomer
      ? [
          "same as last time",
          "repeat previous order",
          "order again",
          "make it like before",
        ]
      : [
          "repeating your previous order?",
          "same as last time?",
          "i remember your order",
          "confirmed ✅",
        ];
  }
  if (PATTERNS.urgency.test(message)) {
    return isCustomer
      ? [
          "it's urgent!",
          "need it asap",
          "please hurry",
          "how fast can you deliver?",
        ]
      : [
          "we'll prioritize this",
          "on it right away",
          "understood, fast delivery",
          "will expedite your order",
        ];
  }
  if (PATTERNS.refund.test(message)) {
    return isCustomer
      ? [
          "i want a refund",
          "how to get money back?",
          "refund not received",
          "cancel and refund",
        ]
      : [
          "refund initiated 💰",
          "will process in 5-7 days",
          "please share order ID",
          "refund policy applies",
        ];
  }
  if (PATTERNS.booking.test(message)) {
    return isCustomer
      ? [
          "I want to book a table",
          "check availability",
          "reserve for 4 people",
          "what time slots available?",
        ]
      : [
          "table booked ✅",
          "what time works for you?",
          "how many people?",
          "reservation confirmed",
        ];
  }
  if (PATTERNS.dietary.test(message)) {
    return isCustomer
      ? [
          "do you have veg options?",
          "is this vegetarian?",
          "any vegan items?",
          "show only veg",
        ]
      : [
          "we have veg/non-veg both",
          "yes, this is vegetarian",
          "check menu filters 👆",
          "veg options available",
        ];
  }
  if (PATTERNS.allergy.test(message)) {
    return isCustomer
      ? [
          "i have allergies",
          "what are the ingredients?",
          "is this nut-free?",
          "contains gluten?",
        ]
      : [
          "let me check ingredients",
          "we can customize for you",
          "please specify allergies",
          "ingredient list available",
        ];
  }
  if (PATTERNS.size.test(message)) {
    return isCustomer
      ? [
          "do you have large size?",
          "can I customize?",
          "what sizes available?",
          "make it medium",
        ]
      : [
          "we have S/M/L sizes",
          "customization available",
          "choose size from menu 👆",
          "size options shown",
        ];
  }
  if (PATTERNS.contact.test(message)) {
    return isCustomer
      ? [
          "how can I contact you?",
          "share phone number",
          "i need support",
          "talk to manager",
        ]
      : [
          "here are our contact details",
          "how can I help you?",
          "phone: ",
          "email: ",
        ];
  }
  if (PATTERNS.timing.test(message)) {
    return isCustomer
      ? [
          "what are your timings?",
          "are you open now?",
          "when do you close?",
          "working hours?",
        ]
      : [
          "we're open 9 AM - 10 PM",
          "currently open",
          "closed on Sundays",
          "open daily",
        ];
  }
  if (PATTERNS.bill.test(message)) {
    return isCustomer
      ? [
          "please send the bill",
          "what is the total amount?",
          "may I have the invoice?",
          "is the bill ready?",
        ]
      : [
          "sending the bill shortly 🧾",
          "total amount: ",
          "here is your invoice",
          "please check the total 💰",
        ];
  }
  if (PATTERNS.delivery.test(message)) {
    return isCustomer
      ? [
          "when will it be delivered?",
          "track my delivery",
          "is it on the way?",
          "how long will it take?",
        ]
      : [
          "your order is on the way 🚗",
          "will be delivered in 20 mins",
          "out for delivery",
          "arriving soon",
        ];
  }
  if (PATTERNS.taste.test(message)) {
    return isCustomer
      ? ["delicious! 😋", "very tasty", "loved it ❤️", "not good 😞"]
      : ["glad you liked it! 😊", "thank you for feedback", "we will improve"];
  }
  if (PATTERNS.positiveFeedback.test(message)) {
    return isCustomer
      ? [
          "excellent! 👌",
          "loved it ❤️",
          "will order again",
          "amazing quality",
        ]
      : [
          "thank you! 🙏🏻",
          "glad you enjoyed",
          "come back soon!",
          "we appreciate your feedback",
        ];
  }
  if (PATTERNS.negativeFeedback.test(message)) {
    return isCustomer
      ? ["not satisfied", "need improvement", "disappointed", "expected better"]
      : [
          "sorry to hear that 🙏🏻",
          "how can we improve?",
          "let us make it right",
          "we apologize",
        ];
  }
  if (PATTERNS.quantity.test(message)) {
    return isCustomer
      ? [
          "add one more",
          "extra please",
          "make it less spicy",
          "can I add items?",
        ]
      : ["sure, added", "updated quantity", "anything else?", "done ✅"];
  }
  if (PATTERNS.location.test(message)) {
    return isCustomer
      ? [
          "share your location",
          "where are you located?",
          "send address",
          "how to reach?",
        ]
      : [
          "sharing location 📍",
          "here is the address",
          "check the map",
          "we are at...",
        ];
  }
  if (PATTERNS.ok.test(message)) return ["ok", "👍🏻", "sure", "got it"];
  if (PATTERNS.confirm.test(message)) {
    return isCustomer
      ? ["yes ✅", "confirmed", "done", "sure 👍🏻"]
      : ["confirmed ✅", "order confirmed", "done", "all set!"];
  }
  if (PATTERNS.cancel.test(message)) {
    return isCustomer
      ? ["cancel order", "no thanks", "not needed", "changed my mind"]
      : ["order cancelled", "no problem", "understood", "cancelled ❎"];
  }
  if (PATTERNS.waiting.test(message)) {
    return isCustomer
      ? ["how long to wait?", "still waiting", "please hurry"]
      : [
          "please wait",
          "will be ready soon",
          "just 5 more minutes",
          "almost done",
        ];
  }
  if (PATTERNS.menu.test(message)) {
    return isCustomer
      ? [
          "please share the menu",
          "do you have a catalog?",
          "what do you serve?",
          "send food list",
        ]
      : [
          "swipe up to view our menu 👆",
          "check our catalog",
          "menu available above",
          "browse items by swiping up 📜",
        ];
  }
  if (PATTERNS.offers.test(message)) {
    return isCustomer
      ? [
          "any ongoing offers?",
          "do you have combos?",
          "best deals today?",
          "discount available?",
        ]
      : [
          "check offers in menu 👆",
          "we have great combos",
          "swipe up to see daily deals",
          "20% off on combos!",
        ];
  }
  if (PATTERNS.specials.test(message)) {
    return isCustomer
      ? [
          "what is special today?",
          "what do you recommend?",
          "what is famous here?",
          "any suggestions?",
        ]
      : [
          "check our Chef's Specials 👩‍🍳",
          "our bestsellers are amazing",
          "highly recommend our specials",
          'see "Recommended" in menu 👆',
        ];
  }
  if (PATTERNS.variety.test(message)) {
    return isCustomer
      ? [
          "what varieties do you have?",
          "show me all options",
          "do you have different types?",
          "any other choices?",
        ]
      : [
          "we have many varieties 📜",
          "check all options by swiping up 👆",
          "browse full variety in menu",
          "filter by category",
        ];
  }
  if (PATTERNS.price.test(message)) {
    return isCustomer
      ? [
          "what is the price?",
          "any discounts available?",
          "is this the final price?",
          "how much for this?",
        ]
      : [
          "price depends on quantity",
          "i will share price details",
          "check prices in catalog 👆",
          "special offer active!",
        ];
  }
  if (PATTERNS.availability.test(message)) {
    return isCustomer
      ? [
          "is it available?",
          "in stock?",
          "when will it be ready?",
          "do you have this?",
        ]
      : [
          "yes, available now",
          "currently in stock",
          "will be ready in 15 mins",
          "available ✅",
        ];
  }
  if (PATTERNS.payment.test(message)) {
    return isCustomer
      ? [
          "how can I pay?",
          "is UPI available?",
          "can I pay in cash?",
          "payment failed, what next?",
        ]
      : [
          "please complete the payment",
          "upi and cash accepted",
          "i will resend payment link",
          "multiple payment options",
        ];
  }
  if (PATTERNS.issue.test(message)) {
    return isCustomer
      ? [
          "my order is delayed",
          "wrong item received",
          "I want to raise a complaint",
          "there is a problem",
        ]
      : [
          "sorry for the inconvenience 🙏🏻",
          "we are checking this issue",
          "i will resolve this shortly",
          "let me help you",
        ];
  }
  if (PATTERNS.thanks.test(message)) {
    return isCustomer
      ? ["thanks!", "thank you!", "appreciate it", "🙏🏻"]
      : ["you're welcome!", "glad to help!", "anytime", "happy to assist"];
  }

  // Priority 4: General patterns
  if (PATTERNS.greeting.test(message)) {
    return isCustomer
      ? ["hi", "hello!", "i need help", "i want to order"]
      : [
          "hello!",
          "how can I assist you?",
          "please let me know your requirement",
        ];
  }
  if (PATTERNS.orderGeneral.test(message)) {
    return isCustomer
      ? [
          "i want to place an order",
          "what is the status of my order?",
          "can I modify my order?",
          "cancel my order",
          "track my order",
        ]
      : [
          "your order is confirmed ✅",
          "order is being prepared",
          "order is ready for pickup",
          "order has been dispatched 🚗",
          "please confirm the order details",
        ];
  }

  // Fallback
  return isCustomer
    ? ["i have a question", "need help", "tell me more"]
    : ["how can I assist?", "please share details", "i'm listening"];
}
